import { getTranslate, lang } from '../localization'

/**
 * Every ticket state, as a runtime value.
 *
 * The union is DERIVED from this rather than written alongside it. Both used
 * to exist: the union here, and a hand-copied `ALL_STATES` in the reachability
 * test, kept in step by nothing. Adding a sixth state would have compiled, and
 * the test would have gone on passing while silently not covering it — the
 * failure mode of a test that maintains its own idea of the domain.
 *
 * Deriving the union from the list is safe in a way that deriving the list
 * from LEGAL_TRANSITIONS' keys would not be. That map is typed
 * `Record<TicketState, ...>`, so its keys agree with the union BY
 * CONSTRUCTION; a test iterating them would be asking the map to confirm
 * itself. This list is the definition both the union and the map answer to,
 * so iterating it still checks something — namely that every state has a
 * non-empty transition list, which no type can express.
 */
export const TICKET_STATES = [
  'NEW',
  'OPEN',
  'PENDING',
  'RESOLVED',
  'CLOSED'
] as const

export type TicketState = (typeof TICKET_STATES)[number]
export type TicketTab = 'MINE' | 'AFFECTING_US' | 'ASSIGNED'
export type TicketSubmissionChannel =
  | 'WEB'
  | 'EMAIL'
  | 'API'
  | 'IN_APP'
  | 'CHAT_FUTURE'
export type TicketCommentScope =
  | 'PARTICIPANT_PUBLIC'
  | 'ADOTEL_INTERNAL'
  | 'REPORTER_AND_ASSIGNEE_ONLY'

export interface TicketEntityRef {
  type: string
  code: string
}

export interface TicketReporter {
  type: string
  userId?: string | null
  displayName?: string | null
  entityType?: string | null
  entityCode?: string | null
  // Resolved by the BFF on the DETAIL path only. Absent on the list, absent
  // when the assets directory cannot answer, and absent for a reporter type it
  // does not hold — entityCode is the fallback in all three cases.
  entityName?: string | null
  serviceCode?: string | null
}

export type TicketContainerResolutionStatus =
  | 'RESOLVED'
  | 'PARTIAL'
  | 'UNAVAILABLE'

export interface Ticket {
  id: string
  ticketNumber: string
  reporter?: TicketReporter | null
  reporterEntityRef?: TicketEntityRef | null
  containerRefs: TicketEntityRef[]
  containerResolutionStatus: TicketContainerResolutionStatus
  createdBy?: { kind: string; principalId: string } | null
  intakeChannel: string
  assigneeUserId: string
  assigneeIsAdotelAdmin: boolean
  watcherUserIds: string[]
  affectedEntityRefs: TicketEntityRef[]
  state: TicketState
  priority: TicketPriority
  submissionChannel: TicketSubmissionChannel
  productScope: string
  sourcePageKey?: string
  relatedRecords?: TicketRelatedRecord[]
  title: string
  version: number
  createdAt: string
  changedAt: string
  hasDiagnosticContext: boolean
  /**
   * Absent for a ticket created before the SLA engine, or whose policy lookup
   * produced nothing. The BFF returns null for the whole block rather than
   * empty deadlines, so `sla` being present means both deadlines are real.
   */
  sla?: TicketSLA | null
}

export interface TicketSLA {
  policyId: string
  policyVersion: number
  respondBy: string
  resolveBy: string
  /**
   * Set once by the server when the breach is observed — never derived here
   * from `respondBy < now`. A client deriving it would disagree with the server
   * across clock skew and across the paused window, and two agents looking at
   * the same ticket would see different states.
   */
  breachedAt?: string | null
  pausedSeconds: number
  /**
   * The promise the deadlines came from.
   *
   * OPTIONAL because only the detail query asks for them — the ticket table
   * spreads the same fragment for up to 50 rows and renders none of this, so
   * they are selected by `TicketDetailFields` alone.
   *
   * Zero means NOT PUBLISHED, not "promised zero seconds". ticket-service joins
   * an immutable policy version at read time and a pruned version yields
   * nothing, which proto3 scalars cannot distinguish from a real zero. No
   * policy would promise zero, so the panel renders nothing for 0.
   */
  respondAfterSeconds?: number | null
  resolveAfterSeconds?: number | null
  /**
   * What the policy IS, since `SLAPolicyVersion` carries no name field.
   *
   * `policyReporterEntityCode` of `'*'` is the platform default; a concrete
   * code is that customer's override. Sent as dimensions rather than a rendered
   * label because this panel is localized — a string composed server-side could
   * only ever be English.
   */
  policyReporterEntityCode?: string | null
  policyReporterEntityType?: string | null
  policyProductScope?: string | null
  policyPriority?: string | null
  policyIntakeChannel?: string | null
}

export interface TicketRelatedRecord {
  id: string
  refType:
    | 'ASSET_ENTITY'
    | 'PROMOTION'
    | 'RATE_PLAN'
    | 'CHANNEL_MAPPING'
    | 'WALLET_TRANSACTION'
  distributionRef?: {
    propertyCode: string
    roomTypeCode?: string | null
    ratePlanCode?: string | null
    channelCode?: string | null
  } | null
  entityRef?: TicketEntityRef | null
  recordId?: string | null
  status: TicketDiagnosticContextStatus
  snapshotVersion: number
  capturedAt?: string | null
}

export type TicketDiagnosticContextStatus =
  | 'PENDING'
  | 'RESOLVING'
  | 'RESOLVED'
  | 'UNAVAILABLE'
  | 'NOT_FOUND'

export interface TicketDiagnosticContext {
  id: string
  status: TicketDiagnosticContextStatus
  snapshotVersion: number
  capturedAt?: string | null
  services: string[]
}

export interface TicketTraceLog {
  date: string
  service: string
  endpointName: string
  endpointURL: string
  status: number
  // No userIP/userId here on purpose — TICKET_TRACE_LOGS_QUERY does not select
  // them, so typing them as present would be a claim the response does not make.
  request: string
  response: string
}

export interface TicketTraceLogPage {
  context: TicketDiagnosticContext
  logs: TicketTraceLog[]
  nextPageToken?: string | null
}

export interface TicketComment {
  /**
   * The author's name, resolved by the BFF from `authorUserId` at read time.
   *
   * Optional for three distinct reasons, all of which fall back to the id: the
   * directory could not answer, the author is not a directory user, or the
   * comment is WITHHELD — the server clears `authorUserId` on those, so there
   * is nothing to resolve and nothing that could be disclosed by resolving it.
   */
  authorDisplayName?: string | null
  id: string
  ticketId: string
  authorUserId: string
  scope: TicketCommentScope
  body: string
  createdAt: string
  redacted: boolean
}

export interface TicketCommentList {
  comments: TicketComment[]
  nextPageToken?: string | null
}

/**
 * What a comment should render as.
 *
 * `redacted` is the ONLY signal that content was withheld — an empty body is
 * not, because a legitimately short comment looks identical and the server
 * clears the body of a withheld one. Branching on the body would show a
 * withheld note as an ordinary blank comment, which is the failure this
 * distinction exists to prevent.
 *
 * There is no 'hidden' case here on purpose: comments the caller may not know
 * exist never arrive at all (ADR-017 §2.8), so the client has nothing to
 * decide about them.
 */
export type CommentDisplay = 'visible' | 'withheld'

/**
 * FAILS CLOSED. Only an explicit `false` shows content; a missing, null or
 * otherwise non-boolean flag is treated as withheld.
 *
 * The obvious `comment.redacted ? 'withheld' : 'visible'` fails OPEN, and
 * measurably so — an absent, null or undefined flag all evaluated to 'visible',
 * meaning a schema change, a partial response, a stale cached shape or a server
 * regression that drops the field turns directly into disclosure. The
 * TypeScript type is a compile-time claim about a payload that arrives at
 * runtime from another service; it cannot enforce anything here.
 *
 * The cost of failing closed is that a dropped field hides content that should
 * have been visible — noisy, obvious, and reversible. The cost of failing open
 * is showing a private note to someone who should not see it. Those are not
 * comparable, so the tie goes to closed.
 */
export function commentDisplay(comment: {
  redacted?: boolean | null
}): CommentDisplay {
  return comment.redacted === false ? 'visible' : 'withheld'
}

/**
 * Whether a cursor-paginated response has a further page.
 *
 * Used by every ticketing list, and the reason none of them checks `.length`:
 * for comments the server filters a page AFTER reading it, so 20 stored rows
 * can arrive as 15 — or 0 — with more behind them. Only an absent token
 * terminates. A caller that stopped on a short page would truncate the
 * conversation precisely on threads containing withheld comments.
 */
export function hasNextPage(
  page?: { nextPageToken?: string | null } | null
): boolean {
  // Accepts an absent page so callers do not each repeat a `|| {}` guard for
  // the pre-first-response render. "No page yet" and "no further page" are the
  // same answer here — neither is known to have more.
  return Boolean(page?.nextPageToken)
}

export interface TicketList {
  tickets: Ticket[]
  nextPageToken?: string | null
}

/**
 * The 5-state workflow graph (ADR-017 §3.2), mirroring ticket-service's
 * `domain.CanTransition`.
 *
 * This is a UI AFFORDANCE, not a security control. The server validates every
 * transition and rejects an illegal one with FAILED_PRECONDITION regardless of
 * what this table says. Its job is to stop an operator being offered a button
 * that can only fail — and if the two ever disagree, the server wins and the
 * user sees a rejection rather than a silently wrong outcome.
 *
 * The graph is duplicated across the client/server boundary because the client
 * cannot ask the server which buttons to render. `ticketWorkflow.test.ts` states
 * the contract explicitly, so a server-side change fails loudly here instead of
 * leaving a button that always errors.
 */
const LEGAL_TRANSITIONS: Record<TicketState, TicketState[]> = {
  NEW: ['OPEN'],
  OPEN: ['PENDING', 'RESOLVED'],
  PENDING: ['OPEN', 'RESOLVED'],
  RESOLVED: ['CLOSED', 'OPEN'],
  CLOSED: ['OPEN']
}

/** The states a ticket may move to from `from`. Empty for an unknown state. */
export function nextStates(from: TicketState): TicketState[] {
  return LEGAL_TRANSITIONS[from] || []
}

/**
 * Does this ticket still want someone to do something?
 *
 * DISTINCT FROM isTerminal, and the difference is the point. isTerminal asks
 * "is this ticket over" — it answers CLOSED only, and drives the dimmed row
 * treatment, where a resolved ticket should still look live because it can be
 * reopened. This asks "does it need attention", and a resolved ticket does not:
 * the work is done and it is waiting to be closed.
 *
 * Conflating them is what made the support badge count resolved tickets as
 * outstanding, so a queue with nothing left to do still showed a number and
 * gave no way to find out why.
 */
export function needsAction(state: TicketState): boolean {
  return state !== 'RESOLVED' && state !== 'CLOSED'
}

/**
 * Is this ticket over? CLOSED only — used to de-emphasise a row.
 *
 * RESOLVED is deliberately NOT terminal: it can be reopened, so its row should
 * still look live. If you want "is there anything left to do", that is
 * needsAction above, and the two answer differently for exactly one state.
 *
 * This comment used to read "true once the ticket needs no further work", which
 * is the confusion that produced the bug: a RESOLVED ticket needs no further
 * work and is not terminal, so a caller reaching for "nothing left to do"
 * picked this and counted resolved tickets as outstanding.
 */
export function isTerminal(state: TicketState): boolean {
  return state === 'CLOSED'
}

/** MUI chip colour for a state. Semantic rather than literal, so a palette
 *  change does not require touching call sites. */
/**
 * Triage priority. Non-null on the wire: ticket-service has no "no priority"
 * state — its own mapper collapses the proto's UNKNOWN into P4 — and the BFF
 * mirrors that, so the schema types this non-null.
 *
 * Kept as a union rather than an enum for the same reason TicketState is: the
 * values arrive as strings and a union costs nothing to compare against them.
 */
/**
 * Why a ticket was resolved.
 *
 * Mirrors ticketpb's recordable set. UNKNOWN is deliberately absent: it is the
 * proto zero value and the exact thing ticket-service rejects, so offering it
 * would put the one guaranteed-to-fail answer in the picker.
 */
export type TicketResolutionCode =
  | 'SOLVED_PERMANENTLY'
  | 'SOLVED_WORKAROUND'
  | 'SOLVED_BY_REPORTER'
  | 'NOT_REPRODUCIBLE'
  | 'NO_FAULT_FOUND'
  | 'DUPLICATE'
  | 'WILL_NOT_FIX'
  | 'WITHDRAWN'

/** In the order an agent is most likely to want them, not alphabetically. */
export const TICKET_RESOLUTION_CODES: TicketResolutionCode[] = [
  'SOLVED_PERMANENTLY',
  'SOLVED_WORKAROUND',
  'SOLVED_BY_REPORTER',
  'NOT_REPRODUCIBLE',
  'NO_FAULT_FOUND',
  'DUPLICATE',
  'WILL_NOT_FIX',
  'WITHDRAWN'
]

export function ticketResolutionLabel(code: TicketResolutionCode): string {
  return getTranslate(`Ticket resolution ${code}`)
}

export type TicketPriority = 'P1' | 'P2' | 'P3' | 'P4'

/**
 * Rank within a P-value, lowest first, for sorting. P1 is the most urgent, so
 * it sorts before P4 — the opposite of the numeric suffix's ordering, which is
 * exactly the trap a bare string compare falls into ('P1' < 'P4' happens to be
 * true, so a lexical sort is RIGHT here by accident and would silently break
 * the day a P10 or an UNKNOWN appears).
 */
export function priorityRank(priority: TicketPriority): number {
  switch (priority) {
    case 'P1':
      return 0
    case 'P2':
      return 1
    case 'P3':
      return 2
    case 'P4':
      return 3
    default:
      // The union is a COMPILE-time constraint; the server can still widen the
      // enum, and an old bundle would then meet a value it has never seen.
      // Ranking it with P3 rather than after P4 is deliberate: a value we do
      // not recognise must not be silently sorted to the bottom of its band,
      // because the one thing we know about it is that we do not know how
      // urgent it is. Mid-pack fails safe; buried does not.
      return 2
  }
}

/**
 * Operator-facing label. The raw P-value is already the vocabulary agents use
 * out loud, so this is deliberately a pass-through today rather than inventing
 * "Critical"/"High" names nobody in the support conversation says — but it is a
 * seam, so a translated label can arrive without touching every call site.
 */
export function ticketPriorityLabel(priority: TicketPriority): string {
  return priority
}

export type StateTone = 'default' | 'primary' | 'secondary'

export function stateTone(state: TicketState): StateTone {
  switch (state) {
    case 'OPEN':
      return 'primary'
    case 'RESOLVED':
      return 'secondary'
    default:
      return 'default'
  }
}

/** Localized operator-facing state label; the protocol value remains unchanged. */
export function ticketStateLabel(state: TicketState): string {
  return getTranslate(`Ticket state ${state}`)
}

/**
 * How a ticket's assignment should be described.
 *
 * Escalation to the Adotel-admin tier CLEARS the assignee id — the tier holds
 * the slot rather than a person. So an empty id means "unassigned" ONLY when
 * `assigneeIsAdotelAdmin` is false. Reading the id alone would render an
 * escalated ticket as unassigned, which are opposite things in a triage queue.
 */
export type AssignmentKind = 'unassigned' | 'adotel-admin' | 'user'

export function assignmentKind(ticket: {
  assigneeUserId: string
  assigneeIsAdotelAdmin: boolean
}): AssignmentKind {
  if (ticket.assigneeIsAdotelAdmin) return 'adotel-admin'
  return ticket.assigneeUserId === '' ? 'unassigned' : 'user'
}

/**
 * Whether a ticket in the L1 queue is waiting on Adotel rather than on the
 * customer.
 *
 * The queue spans every tenant, so it is only useful if an agent can see at a
 * glance which rows are theirs to act on. Two cases qualify:
 *
 *   - escalated to the Adotel tier and not yet picked up by a person
 *   - not assigned to anyone at all
 *
 * PENDING is deliberately excluded even when escalated: it means the ticket is
 * waiting on the customer, so surfacing it as actionable would bury the rows
 * that genuinely are. CLOSED is excluded for the obvious reason.
 *
 * RESOLVED is deliberately NOT excluded, and it is the one debatable call here.
 * By the "waiting on Adotel" reading it arguably should be — resolved work is
 * done and the customer is the one left to accept or reopen. It stays in
 * because RESOLVED is reopenable (see nextStates) and an unassigned resolved
 * ticket is a state an agent may still need to see. Flagged for the owner
 * rather than settled here; the tests pin the current behaviour so a change is
 * a decision, not a drift.
 */
export function needsAgentAttention(ticket: {
  state: TicketState
  assigneeUserId: string
  assigneeIsAdotelAdmin: boolean
}): boolean {
  if (ticket.state === 'CLOSED' || ticket.state === 'PENDING') return false
  return assignmentKind(ticket) !== 'user'
}

/**
 * Renders an ISO timestamp as `YYYY/MM/DD HH:MM:SS AM`.
 *
 * Assembled from parts rather than via toLocaleString, because no locale
 * produces that field ORDER: en-US gives month-first (`8/29/2026`), most others
 * day-first. The order is fixed deliberately — these timestamps are read down a
 * column in a conversation and an audit trail, where a big-endian date sorts and
 * scans correctly and an ambiguous 08/09 does not.
 *
 * What stays LOCALE-dependent is everything inside the fields: under /fa the
 * parts come back on the Persian calendar with Persian digits, and the day
 * period is «ق.ظ»/«ب.ظ» rather than AM/PM. Only the arrangement is ours.
 *
 * Zero-padded, so the column aligns; 12-hour with an explicit day period, as
 * requested.
 *
 * Falls back to the raw value rather than throwing — a malformed date must not
 * blank the whole row.
 */
/**
 * The one formatter formatWhen uses, built once.
 *
 * `lang` is bound at module scope, so there is exactly one shape per module
 * instance and nothing to key a cache on. Constructing an Intl.DateTimeFormat
 * is among the more expensive built-in constructions — locale and calendar
 * negotiation — and a full ticket detail renders one timestamp per comment
 * plus one per activity entry, so this was rebuilding the same object ~150
 * times per page.
 *
 * `hourCycle: 'h12'` rather than `hour12: true`, and the difference is not
 * cosmetic. `hour12` leaves the CYCLE to the locale: en-US defaults to h12
 * (1-12) but fa-IR defaults to h11 (0-11), so noon rendered as "12:05:07 PM"
 * in English and "۰۰:۰۵:۰۷ بعدازظهر" — hour zero, marked PM — in Persian. A
 * fixed HH:MM:SS contract that changes meaning with the reader's language is
 * not fixed. h12 is a no-op for English and corrects Persian.
 */
let cachedExactFormatter: Intl.DateTimeFormat | null = null
function exactFormatter(): Intl.DateTimeFormat {
  if (!cachedExactFormatter) {
    cachedExactFormatter = new Intl.DateTimeFormat(
      lang === 'fa' ? 'fa-IR-u-ca-persian' : 'en-US',
      {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h12'
      }
    )
  }
  return cachedExactFormatter
}

export function formatWhen(iso: string): string {
  if (!iso) return '—'
  const parsed = new Date(iso)
  if (isNaN(parsed.getTime())) return iso

  const parts = exactFormatter().formatToParts(parsed)

  const field = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? ''

  const date = `${field('year')}/${field('month')}/${field('day')}`
  const time = `${field('hour')}:${field('minute')}:${field('second')}`
  // The day period is appended only when the locale supplies one. Trimming
  // instead would leave a trailing space that shows up in text assertions and
  // in copied text.
  const period = field('dayPeriod')
  return period ? `${date} ${time} ${period}` : `${date} ${time}`
}

/**
 * How long ago, in the reader's language.
 *
 * The screen printed absolute timestamps to the second, about eight times per
 * ticket — `28/08/2026, 15:50:05`. Nobody triaging a queue needs the second,
 * and every one of those forced the reader to subtract from the current time to
 * learn the only thing they wanted: is this old. The absolute form is still
 * exact and still available; it moves to a tooltip, where precision is
 * consulted rather than parsed.
 *
 * Intl.RelativeTimeFormat rather than hand-built strings: it carries the plural
 * rules and the Persian digits, and getting "۲ ساعت پیش" right by hand is how
 * the digit-system bugs in localizeCount happened.
 *
 * `now` is injected rather than read inside, for the same reason every other
 * time-dependent function in this file takes it — a relative formatter that
 * reads the clock cannot be pinned by a test.
 */
export function relativeWhen(iso: string, now: Date = new Date()): string {
  if (!iso) return '—'
  const parsed = new Date(iso)
  if (isNaN(parsed.getTime())) return iso

  const seconds = Math.round((parsed.getTime() - now.getTime()) / 1000)
  const magnitude = Math.abs(seconds)

  // Largest unit that still yields a number worth reading. Below a minute the
  // answer is "just now" in every language that matters here — RelativeTimeFormat
  // would say "in 3 seconds", which is noise about a ticket.
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60]
  ]

  const formatter = new Intl.RelativeTimeFormat(
    lang === 'fa' ? 'fa-IR' : undefined,
    { numeric: 'auto' }
  )

  // Pick the unit by magnitude, then TRUNCATE within it.
  //
  // Two bugs live here and the obvious fix for one causes the other.
  //
  // Rounding inside a magnitude-selected bucket overflows it: 3599s is under an
  // hour so it picks `minute`, and Math.round(3599/60) is 60 — "in 60 minutes".
  // Likewise 86399s said "24 hours" and 604799s "7 days".
  //
  // Selecting by the rounded value instead fixes those and breaks the middle of
  // every bucket, which is worse and shipped: Math.round(4 days / 1 week) is 1,
  // so a comment four days old read "last week". 45 seconds read "1 minute ago".
  //
  // Truncating cannot do either. The bucket is chosen only when
  // `magnitude >= size`, and a larger bucket was already rejected, so
  // `magnitude < nextSize` — which bounds the truncated value below
  // `nextSize / size`. An hour can never report 24, a minute can never report
  // 60, and nothing rounds up into a unit it does not belong to.
  for (const [unit, size] of units) {
    if (magnitude >= size) {
      return formatter.format(Math.trunc(seconds / size), unit)
    }
  }
  // 'second', not 'minute'. With numeric:'auto' the zero case is the phrase the
  // locale uses for the present moment — "now" for seconds, "this minute" for
  // minutes. A comment posted forty seconds ago was "this minute", which reads
  // like a calendar entry rather than an answer to "how long ago".
  return formatter.format(0, 'second')
}

/**
 * Shortens an opaque identifier for display.
 *
 * Assignee ids reach the client encoded by the BFF's id codec — `usr_` plus
 * ~60 characters of unbreakable ciphertext. Printed whole, one of them is wider
 * than the column that holds it and shoves the rest of the row sideways.
 *
 * There is no name lookup to use instead: the ticket carries an id and nothing
 * resolves it to a person. So the honest display is a recognisable prefix, with
 * the full value kept in `title` so it can still be read and copied. If a user
 * directory ever backs this column, this helper should disappear rather than
 * grow.
 */
export function shortIdentifier(value: string, keep = 12): string {
  // Tolerate a missing value rather than throwing. This renders inside a list
  // row, and an exception here blanks the entire screen over one absent field —
  // the same reason formatWhen falls back instead of throwing. The first
  // version of this helper did throw, and took out four list tests whose
  // fixtures simply omit assigneeUserId.
  if (!value) return ''
  if (value.length <= keep) return value
  return `${value.slice(0, keep)}…`
}

/**
 * Describes the assignment in one phrase. Delegates the decision to
 * `assignmentKind` so the escalation rule is stated in exactly one place.
 *
 * The user branch returns the id SHORTENED — see shortIdentifier. Callers that
 * need the full value for a tooltip should read `assigneeUserId` directly.
 */
export function assignmentLabel(ticket: {
  assigneeUserId: string
  assigneeIsAdotelAdmin: boolean
}): string {
  switch (assignmentKind(ticket)) {
    case 'adotel-admin':
      return getTranslate('Escalated to Adotel')
    case 'user':
      return shortIdentifier(ticket.assigneeUserId)
    default:
      return getTranslate('Unassigned')
  }
}

/** Localized operator-facing comment scope; the protocol value stays intact. */
export function ticketCommentScopeLabel(scope: TicketCommentScope): string {
  return getTranslate(`Ticket comment scope ${scope}`)
}

/** The three list tabs, in the order ADR-017 §2.6's UI presents them. */
export const TICKET_TABS: { value: TicketTab; label: string }[] = [
  { value: 'MINE', label: 'Mine' },
  { value: 'AFFECTING_US', label: 'Affecting us' },
  { value: 'ASSIGNED', label: 'Assigned' }
]

/**
 * The same labels keyed by tab value, for screens that render one list rather
 * than the whole strip.
 *
 * Derived from TICKET_TABS rather than written out again: two hand-maintained
 * copies of the same three strings drift the moment one is renamed.
 */
export const TICKET_TAB_LABEL: Record<TicketTab, string> = TICKET_TABS.reduce(
  (labels, tab) => ({ ...labels, [tab.value]: tab.label }),
  {} as Record<TicketTab, string>
)

export type SLAUrgency = 'breached' | 'due-soon' | 'on-track' | 'none'

/**
 * How close a ticket is to its respond deadline, as one value the UI can render
 * without doing date arithmetic in a component.
 *
 * `now` is injected rather than read from the clock inside, so the thresholds
 * are testable and two renders in the same tick agree. This mirrors the
 * service-side rule that time-dependent decisions take their instant as input.
 *
 * A CLOSED or RESOLVED ticket reports 'none': its deadline has stopped mattering,
 * and showing "overdue" against work already finished trains agents to ignore
 * the indicator — the same signal-destruction the SLA numbers themselves are
 * exposed to.
 */
export function slaUrgency(
  ticket: Pick<Ticket, 'state' | 'sla'>,
  now: Date,
  dueSoonMinutes = 60
): SLAUrgency {
  const sla = ticket.sla
  if (!sla) return 'none'
  if (ticket.state === 'RESOLVED' || ticket.state === 'CLOSED') return 'none'
  if (sla.breachedAt) return 'breached'

  const respondBy = new Date(sla.respondBy)
  if (isNaN(respondBy.getTime())) return 'none'

  const msRemaining = respondBy.getTime() - now.getTime()
  // Past the deadline but not yet swept: the breach worker has not run. Show it
  // as breached rather than as "due in -3 minutes", which reads as a bug.
  if (msRemaining <= 0) return 'breached'
  if (msRemaining <= dueSoonMinutes * 60 * 1000) return 'due-soon'
  return 'on-track'
}

/** Chip tone for an urgency, using the same vocabulary as `stateTone`. */
export function slaTone(urgency: SLAUrgency): StateTone {
  switch (urgency) {
    case 'breached':
      return 'secondary'
    case 'due-soon':
      return 'primary'
    default:
      return 'default'
  }
}

/**
 * Rank for triage ordering. Lower sorts first.
 *
 * The queue's natural order is `changedAt` descending, which is a FEED: a
 * ticket nobody has touched sinks, which is the opposite of what a queue is
 * for. This ranks by what needs attention instead.
 *
 * Settled tickets rank last regardless of SLA. `slaUrgency` already returns
 * 'none' for RESOLVED and CLOSED, so without this they would tie with healthy
 * open tickets and interleave with them.
 *
 * NOT `isTerminal`, which is CLOSED-only — RESOLVED is reopenable in this
 * workflow and so is not terminal. But it is not triage work either: nobody
 * needs to pick it up. The predicate for "sinks in a queue" and the predicate
 * for "cannot be transitioned further" are different questions, and using the
 * second for the first leaves resolved tickets interleaved with live ones.
 * This deliberately matches the states slaUrgency treats as having no clock.
 */
export function triageRank(ticket: Ticket, now: Date): number {
  if (ticket.state === 'RESOLVED' || ticket.state === 'CLOSED') return 4
  switch (slaUrgency(ticket, now)) {
    case 'breached':
      return 0
    case 'due-soon':
      return 1
    default:
      // Unassigned work outranks assigned work at the same urgency: someone is
      // already looking at the assigned one.
      return assignmentKind(ticket) === 'unassigned' ? 2 : 3
  }
}

/**
 * `changedAt` descending, treating an unparseable timestamp as a tie.
 *
 * The obvious `new Date(b).getTime() - new Date(a).getTime()` returns NaN when
 * either side does not parse, and NaN is not <0, >0 or 0 — so the comparator
 * stops being a total order and Array.sort's result becomes engine-defined for
 * the whole array, not just those two rows.
 *
 * Reachable, not theoretical: isTicket validates `changedAt` with isString, so
 * '' or any non-ISO string passes validation and arrives here. formatWhen in
 * this same file already guards exactly this way; the first version of this
 * sort did not follow that precedent.
 *
 * Returning 0 keeps the pair in their existing relative order rather than
 * inventing one.
 */
function compareChangedAtDesc(b: string, a: string): number {
  const bt = new Date(b).getTime()
  const at = new Date(a).getTime()
  if (isNaN(bt) || isNaN(at)) return 0
  return bt - at
}

/**
 * Orders a page of tickets for triage: breached, due soon, unassigned,
 * assigned, then terminal. Ties keep `changedAt` descending, so the ordering
 * within a band is the one the list had before.
 *
 * SORTS THE LOADED PAGE, NOT THE RESULT SET. `tickets()` takes no sort
 * argument, so the server still pages by its own order and a more urgent
 * ticket can sit on a page that has not been loaded. That is a real limit, not
 * a rounding error — but a partial sort that puts breached tickets at the top
 * of what you are looking at still beats reverse-chronological. Server-side
 * ordering is the fix, and it needs a resolver change.
 *
 * Returns a new array; callers render from it and the cache is untouched.
 */
export function sortForTriage(tickets: Ticket[], now: Date): Ticket[] {
  return [...tickets].sort((a, b) => {
    const rank = triageRank(a, now) - triageRank(b, now)
    if (rank !== 0) return rank
    // Priority breaks ties WITHIN an urgency band, never across one. A breached
    // P4 still outranks an on-track P1, because a missed commitment is a fact
    // about this ticket while priority is a prediction made at creation — and
    // letting priority jump bands would bury the breach the SLA engine exists
    // to surface.
    const byPriority = priorityRank(a.priority) - priorityRank(b.priority)
    if (byPriority !== 0) return byPriority
    return compareChangedAtDesc(b.changedAt, a.changedAt)
  })
}

// ─── activity timeline ──────────────────────────────────────────────────────

export interface TicketActivityUser {
  email: string
  role: string
}

export interface TicketActivityEntry {
  action: string
  occurredAt: string
  level: string
  user: TicketActivityUser
  logContent: { data: string | null } | null
}

export interface TicketActivityPage {
  totalNumberOfLogs: number
  logs: TicketActivityEntry[]
}

/**
 * The audit `action` is the BFF resolver's Go method name — `TransitionTicket`,
 * `AddTicketWatcher`. Those are the store's vocabulary, not an operator's, and
 * they reach the user unchanged unless something maps them.
 *
 * Keys are the English sentence rather than the enum, because that is this
 * panel's localization convention: `getTranslate` falls back to the key, so an
 * action added service-side later reads as English prose instead of rendering
 * blank. The unmapped case returns null rather than a placeholder, so the
 * caller decides whether to show a row at all.
 */
export function ticketActivityLabel(action: string): string | null {
  switch (action) {
    case 'CreateTicket':
      return 'opened this ticket'
    case 'TransitionTicket':
      return 'changed the state'
    case 'AssignTicket':
      return 'changed the assignee'
    case 'AddTicketComment':
      return 'added a comment'
    case 'AddTicketWatcher':
      return 'added a watcher'
    case 'RemoveTicketWatcher':
      return 'removed a watcher'
    case 'TagTicketEntity':
      return 'tagged an affected entity'
    case 'RefreshTicketTraceContext':
      return 'refreshed the diagnostic context'
    default:
      return null
  }
}

/**
 * The audit record's `data` is a JSON STRING, not an object — `LogContent.data`
 * is `String` in the schema. It is written by the BFF and is therefore trusted
 * for shape but not for presence: a record predating a field, or an error-level
 * record whose payload is the failure, both parse to something without it.
 *
 * Returns a short suffix for the one-line entry, or '' when the payload says
 * nothing worth showing. Never throws — a malformed record must not take the
 * whole timeline down with it, because the timeline is the thing you read WHEN
 * something has already gone wrong.
 */
export function ticketActivityDetail(
  action: string,
  raw: string | null | undefined
): string {
  if (!raw) return ''
  let data: Record<string, unknown>
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return ''
    data = parsed as Record<string, unknown>
  } catch {
    return ''
  }
  const str = (key: string): string =>
    typeof data[key] === 'string' ? (data[key] as string) : ''

  switch (action) {
    case 'TransitionTicket':
      return str('to')
    case 'AddTicketComment':
      return str('scope')
    case 'AddTicketWatcher':
    case 'RemoveTicketWatcher':
      return str('watcherUserId')
    case 'CreateTicket':
      return str('productScope')
    default:
      return ''
  }
}

// ─── bulk assignment ────────────────────────────────────────────────────────

export type TicketBulkAssignTarget = 'CALLER' | 'ADOTEL_ADMIN'

export interface TicketBulkAssignResult {
  ticketId: string
  ok: boolean
  code?: string | null
}

/**
 * What to tell the operator a gRPC code MEANT, in their terms.
 *
 * `Aborted` is the one that matters: it is not a fault, it is the optimistic
 * concurrency token doing its job — someone else changed that ticket between
 * the list loading and the button being pressed. Reported as an error it reads
 * as a bug; reported as "someone else got there first" it reads as what
 * happened, and the remedy (reload, look again) follows from it.
 *
 * An unrecognised code returns null so the caller can fall back to a count
 * rather than inventing a cause it does not know.
 */
export function bulkAssignFailureReason(code?: string | null): string | null {
  switch (code) {
    case 'Aborted':
    case 'FailedPrecondition':
      return 'changed since the list loaded'
    case 'NotFound':
      return 'no longer visible to you'
    case 'PermissionDenied':
      return 'not yours to assign'
    case 'DeadlineExceeded':
    case 'Canceled':
      return 'timed out'
    default:
      return null
  }
}

export interface BulkAssignSummary {
  assigned: number
  failed: number
  /** Distinct human reasons, in first-seen order. Empty when none is known. */
  reasons: string[]
  /**
   * Failures whose code mapped to no reason.
   *
   * Tracked separately because `reasons` alone cannot say whether it covers
   * every failure. Three stale rows and two unknown ones produce a single
   * reason, and rendering "5 not assigned — changed since the list loaded"
   * attributes all five to a cause that explains three — telling the operator
   * to retry when two have a different, possibly persistent problem.
   */
  unexplained: number
}

/**
 * Collapse per-ticket results into what a person needs to read.
 *
 * Counts AND causes, because either alone misleads. "2 failed" gives an
 * operator nothing to do; "changed since the list loaded" without a count hides
 * how much of the batch landed. Partial application is the normal outcome here,
 * so the summary has to make it legible rather than treat it as an error.
 */
export function summarizeBulkAssign(
  results: TicketBulkAssignResult[]
): BulkAssignSummary {
  const reasons: string[] = []
  let assigned = 0
  let failed = 0
  let unexplained = 0
  results.forEach((result) => {
    if (result.ok) {
      assigned += 1
      return
    }
    failed += 1
    const reason = bulkAssignFailureReason(result.code)
    if (!reason) {
      unexplained += 1
      return
    }
    // De-duplicated: five tickets that all went stale are one fact, not five.
    if (!reasons.includes(reason)) reasons.push(reason)
  })
  return { assigned, failed, reasons, unexplained }
}

/**
 * A promised duration, said the way a person would say it.
 *
 * Returns null for 0 and for absent, and those are the SAME case on purpose:
 * the wire cannot distinguish "no policy version found" from "a policy
 * promising zero seconds", and no policy would promise zero. Rendering
 * "within 0 seconds" would state a commitment nobody made.
 *
 * Whole units only — a service-level promise is authored in round numbers
 * (4 hours, 10 days), so "within 1.5 days" would be arithmetic the policy
 * never said. Falls back to the next unit down rather than rounding.
 */
/**
 * Largest first, because a promise is read in the biggest unit that fits.
 *
 * Module scope rather than a literal inside the function: it is constant, it
 * was being rebuilt on every call, and both passes below iterate the SAME
 * table — having one declaration makes that a fact rather than a coincidence
 * two edits could break apart.
 */
const PROMISE_UNITS: ReadonlyArray<readonly [number, string, string]> = [
  [86400, 'day', 'days'],
  [3600, 'hour', 'hours'],
  [60, 'minute', 'minutes']
] as const

/**
 * The phrase, with singular and plural as SEPARATE dictionary keys.
 *
 * Not a formatting nicety: the dictionary is keyed on the English string, so
 * "within 1 hour" and "within 2 hours" are two entries and a shared key would
 * leave one of them untranslated on the Persian panel.
 */
function withinPhrase(value: number, singular: string, plural: string): string {
  return getTranslate(`within {count} ${value === 1 ? singular : plural}`, {
    count: String(value)
  })
}

export function promisedWithin(seconds?: number | null): string | null {
  if (!seconds || seconds <= 0) return null
  for (const [size, singular, plural] of PROMISE_UNITS) {
    if (seconds >= size && seconds % size === 0) {
      return withinPhrase(seconds / size, singular, plural)
    }
  }
  // Not a whole number of any unit — say the largest one that fits, truncated,
  // rather than inventing precision. Rare: policies are authored in round
  // numbers, and this exists so an odd value degrades instead of disappearing.
  //
  // A second pass rather than a relaxed guard in the first: the first must
  // prefer an EXACT smaller unit over a truncated larger one, so 5400s reads
  // "90 minutes" and not "1 hour". Merging them would lose that ordering.
  for (const [size, singular, plural] of PROMISE_UNITS) {
    if (seconds >= size) {
      return withinPhrase(Math.trunc(seconds / size), singular, plural)
    }
  }
  return getTranslate('within {count} seconds', { count: String(seconds) })
}

/**
 * Which policy applied, in words rather than as a token.
 *
 * The panel rendered `policyId` verbatim — a ~60-character opaque string — and
 * an operator learned nothing from it. `SLAPolicyVersion` has no name, so the
 * label is composed from the dimensions that actually distinguish one policy
 * from another.
 *
 * Returns null when the dimensions are absent, which is the pruned-version
 * case. The caller then falls back to the id, because an unlabelled policy is
 * still better identified by its id than by nothing.
 */
export function slaPolicyLabel(sla: TicketSLA): string | null {
  const parts: string[] = []
  const code = sla.policyReporterEntityCode
  if (code === '*') {
    parts.push(getTranslate('Platform default'))
  } else if (code) {
    // The entity's own code, which is what a customer recognises — it is the
    // same string they see on their own tickets.
    parts.push(getTranslate('{code} override', { code }))
  }
  // '*' is a wildcard in every one of these too, and a wildcard is not
  // information: "any priority" tells a reader nothing they did not already
  // know from the ticket in front of them.
  for (const value of [
    sla.policyPriority,
    sla.policyProductScope,
    sla.policyIntakeChannel
  ]) {
    if (value && value !== '*') parts.push(value)
  }
  return parts.length ? parts.join(' · ') : null
}
